/**
 * Date Range Selector
 *
 * Unified date range selector with quick presets and custom range picker.
 * Used across Account, Performance Lab Pro, Affiliate Dashboard, LP Dashboard, and Admin.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
} from "@/components/ui/popover";
import { QuickPresets } from "./quick-presets";
import { CustomPopoverContent } from "./custom-popover";
import type {
  DateRangeSelectorProps,
  QuickPreset,
  ExtendedPreset,
  CustomDateRange,
} from "./types";

export function DateRangeSelector({
  value,
  onChange,
  className,
}: DateRangeSelectorProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const handlePresetClick = (preset: QuickPreset) => {
    onChange(preset);
    setIsCustomOpen(false); // Close popover if open
  };

  const handleExtendedPresetSelect = (preset: ExtendedPreset) => {
    onChange(preset);
  };

  const handleCustomRangeApply = (range: CustomDateRange) => {
    onChange(range);
  };

  return (
    <div className={cn("inline-flex", className)}>
      <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
        <QuickPresets
          value={value}
          onPresetClick={handlePresetClick}
          onCustomClick={() => setIsCustomOpen(true)}
          isCustomOpen={isCustomOpen}
        />
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-auto p-0 bg-card border border-border shadow-lg"
        >
          <CustomPopoverContent
            value={value}
            onExtendedPresetSelect={handleExtendedPresetSelect}
            onCustomRangeApply={handleCustomRangeApply}
            onClose={() => setIsCustomOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
